import React, { useState } from 'react'
import {
  Hammer, Loader2, History as HistoryIcon, Trash2, FileCode2, ChevronDown, ChevronRight,
} from 'lucide-react'
import Markdown from './Markdown'
import DocumentViewer from './DocumentViewer'
import { useProjects } from '../hooks/useRag'
import {
  useImpactAnalysis, useImpactHistory, useImpactRun, useDeleteImpactRun,
} from '../hooks/useResearch'

// C2 — Impact analysis (doc/plan-sources-expansion.md): paste change notes
// (meeting minutes, a change request), pick the project holding the indexed
// CODEBASE, and get a per-item report: how it works today, affected files,
// change surface, risks — grounded in retrieved code. Runs persist (history).

const CONF_STYLE = {
  high: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  medium: 'bg-gold/15 text-gold-deep dark:text-gold',
  low: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
}

export default function ImpactPanel() {
  const projectsQuery = useProjects()
  const [project, setProject] = useState('')
  const [notes, setNotes] = useState('')
  const [viewer, setViewer] = useState(null) // { source, chunkIndex } for the doc viewer
  const impact = useImpactAnalysis()
  const historyQ = useImpactHistory()
  const openRun = useImpactRun()
  const delRun = useDeleteImpactRun()

  // Open the indexed code file behind an affected-file chip, highlighting the
  // exact chunk the analysis retrieved. The run carries its own project, so a
  // reopened history run opens against the project it was analyzed in.
  const runProject = openRun.data?.project ?? impact.data?.project ?? project
  const openDoc = (source, chunkIndex) => source && setViewer({ source, chunkIndex })

  const run = () => {
    if (!notes.trim() || !project) return
    openRun.reset()
    impact.mutate({ project, change_text: notes.trim() })
  }
  const data = openRun.data ?? impact.data

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={project}
          onChange={(e) => setProject(e.target.value)}
          className="h-10 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
          title="Project holding the indexed codebase"
        >
          <option value="">Codebase project…</option>
          {(projectsQuery.data?.projects ?? []).map((p) => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
        <span className="text-xs text-ink-soft dark:text-mist/50">
          Index a repo first: Sources → Add → Index a codebase.
        </span>
      </div>

      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={6}
        placeholder={'Paste change notes — meeting minutes, a change request, a bug list…\nE.g.: "Customers want CSV export of monthly reports. Also the discount calculation must respect the new VAT rules from July."'}
        className="w-full rounded-xl px-4 py-3 text-sm bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
      />
      <div className="flex items-center gap-3">
        <button
          onClick={run}
          disabled={!notes.trim() || !project || impact.isPending}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md text-sm font-medium border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 dark:text-gold"
        >
          {impact.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />}
          Analyze impact
        </button>
        {impact.isPending && (
          <span className="text-sm text-ink-soft dark:text-mist/60">
            Decomposing the notes and weighing each change item against the code… (~1–3 min)
          </span>
        )}
      </div>
      {impact.isError && <p className="text-sm text-red-600">{impact.error?.message}</p>}

      {data?.items?.length > 0 && (
        <div className="space-y-3">
          {data.items.map((item, i) => (
            <ImpactItem key={i} item={item} onViewDocument={openDoc} />
          ))}
        </div>
      )}

      {(historyQ.data?.history?.length ?? 0) > 0 && (
        <section className="space-y-1.5">
          <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold flex items-center gap-1.5">
            <HistoryIcon className="w-3.5 h-3.5" /> Impact history
          </h3>
          {historyQ.data.history.map((h) => (
            <div key={h.id} className="flex items-center gap-2 text-sm">
              <button
                onClick={() => { setProject(h.project); setNotes(h.query); openRun.mutate(h.id) }}
                className="flex-1 min-w-0 text-left truncate rounded-lg border border-parchment-border bg-white/50 px-3 py-1.5 hover:border-gold text-ink dark:text-mist dark:border-midnight-600 dark:bg-midnight-800/40"
                title="Reopen this report (no re-analysis)"
              >
                <span className="text-xs text-gold-deep dark:text-gold mr-2">{h.project}</span>
                {h.query.slice(0, 90)}
                <span className="ml-2 text-xs text-ink-soft dark:text-mist/50">
                  {new Date(h.created_at).toLocaleString()}
                </span>
              </button>
              <button
                onClick={() => delRun.mutate(h.id)}
                className="p-1.5 rounded text-ink-soft hover:text-red-600 dark:text-mist/50"
                aria-label="Delete report"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </section>
      )}

      {viewer && (
        <DocumentViewer
          project={runProject}
          source={viewer.source}
          highlightChunkIndex={viewer.chunkIndex}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  )
}

function ImpactItem({ item, onViewDocument }) {
  const [open, setOpen] = useState(true)
  return (
    <article className="rounded-xl border border-parchment-border bg-white/70 dark:border-midnight-600 dark:bg-midnight-800/60">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span className="font-display text-base text-ink dark:text-mist flex-1">{item.title}</span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide ${CONF_STYLE[item.confidence] ?? CONF_STYLE.low}`}>
          {item.confidence} confidence
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 text-sm">
          {item.note && <p className="italic text-ink-soft dark:text-mist/60">{item.note}</p>}
          {item.current_behavior && (
            <Section label="How it works today"><Markdown>{item.current_behavior}</Markdown></Section>
          )}
          {item.affected_files?.length > 0 && (
            <Section label="Affected files">
              <ul className="space-y-1">
                {item.affected_files.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <button
                      onClick={() => onViewDocument?.(f.source, f.chunk_index)}
                      className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded bg-gold/10 text-gold-deep hover:bg-gold/20 dark:text-gold font-mono text-xs"
                      title={`Open ${f.source}`}
                    >
                      <FileCode2 className="w-3 h-3" /> {f.file}
                    </button>
                    <span className="text-ink-soft dark:text-mist/60">{f.why}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          <RippleSection ripple={item.ripple_effects} />
          {item.change_surface && (
            <Section label="Change surface"><Markdown>{item.change_surface}</Markdown></Section>
          )}
          {item.risks && (
            <Section label="Risks & open questions"><Markdown>{item.risks}</Markdown></Section>
          )}
        </div>
      )}
    </article>
  )
}

// Verdict colors for ripple v2 call-site classification.
const VERDICT_STYLE = {
  breaking: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  'needs-adaptation': 'bg-gold/15 text-gold-deep dark:text-gold',
  safe: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
}

function RippleSection({ ripple }) {
  // Tolerate both shapes: ripple v2 { method, max_hops, symbols[] } and old
  // history entries that stored a bare list of { symbol, callers }.
  const groups = Array.isArray(ripple) ? ripple : (ripple?.symbols ?? [])
  if (!groups.length) return null
  const method = Array.isArray(ripple) ? 'csv' : ripple?.method
  const hops = Array.isArray(ripple) ? 1 : ripple?.max_hops ?? 1
  const label = method === 'graph'
    ? `Ripple effects — call graph, up to ${hops} hop${hops > 1 ? 's' : ''}`
    : 'Ripple effects (call graph, direct callers)'
  return (
    <Section label={label}>
      <div className="space-y-2">
        {groups.map((r, i) => (
          <div key={i} className="flex flex-wrap items-baseline gap-1.5">
            <span className="font-mono text-xs text-ink dark:text-mist">{r.symbol}</span>
            <span className="text-xs text-ink-soft dark:text-mist/50">← called by</span>
            {r.callers.map((c, j) => (
              <span
                key={j}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-xs ${
                  VERDICT_STYLE[c.verdict] ?? 'bg-cyan-glow/10 text-cyan-800 dark:text-cyan-300'
                }`}
                title={[
                  c.line ? `${c.file}:${c.line}` : c.file,
                  c.depth > 1 ? `${c.depth} hops` : 'direct',
                  c.reason,
                ].filter(Boolean).join(' · ')}
              >
                {c.caller}
                {c.depth > 1 && <span className="opacity-60">·{c.depth}</span>}
              </span>
            ))}
          </div>
        ))}
      </div>
    </Section>
  )
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1">{label}</div>
      {children}
    </div>
  )
}
